/*
 * "App-like" product page behaviour (sections/main-product.liquid + assets/product-app.css)
 *
 *  <product-app-bar>  Fixed bottom bar on phones. It mirrors the live price and the state of the real
 *                     "Add to cart" button and forwards taps to that button, so all of Dawn's cart logic
 *                     (product-form.js, cart drawer / notification, error handling) keeps working untouched.
 *  <product-app-ui>   Floating back / share buttons and the page dots for the swipe gallery.
 *
 * Both are custom elements, so they re-initialise automatically when product-info.js swaps the DOM
 * (e.g. when a customer picks another product from a "combined listing").
 */

(function () {
  const clean = (node) => (node ? node.textContent.replace(/\s+/g, ' ').trim() : '');

  /* --------------------------------------------------------------------------------------------
   * Fixed bottom bar
   * ------------------------------------------------------------------------------------------ */
  if (!customElements.get('product-app-bar')) {
    customElements.define(
      'product-app-bar',
      class ProductAppBar extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.sectionId;
          this.root = this.closest('product-info');
          this.source = this.root && this.root.querySelector(`#ProductSubmitButton-${this.sectionId}`);
          this.button = this.querySelector('.pp-app-bar__button');
          if (!this.root || !this.source || !this.button) return;

          this.text = this.querySelector('.pp-app-bar__text');
          this.priceWrap = this.querySelector('.pp-app-bar__price');
          this.now = this.querySelector('.pp-app-bar__now');
          this.was = this.querySelector('.pp-app-bar__was');
          this.toast = this.querySelector('.pp-app-bar__toast');
          this.addedText = this.dataset.addedText || 'Added to cart';

          this.onClick = () => this.forwardClick();
          this.button.addEventListener('click', this.onClick);

          // keep the bar in step with the real button and the price block
          this.observer = new MutationObserver(() => this.sync());
          this.observer.observe(this.source, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'class'],
            childList: true,
            characterData: true,
            subtree: true,
          });
          const price = this.root.querySelector(`#price-${this.sectionId}`);
          if (price) {
            this.observer.observe(price, {
              attributes: true,
              attributeFilter: ['class'],
              childList: true,
              characterData: true,
              subtree: true,
            });
          }

          // cart events published by Dawn (pubsub.js)
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribers = [
              subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                if (event && event.source === 'product-form') this.flashAdded();
              }),
              subscribe(PUB_SUB_EVENTS.cartError, (event) => {
                if (event && event.source === 'product-form') this.showError(event);
              }),
            ];
          }

          // keep the page content clear of the fixed bar
          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => this.setBarHeight());
            this.resizeObserver.observe(this);
          }

          this.sync();
          this.setAttribute('data-ready', '');
          this.root.classList.add('pp-app-ready');
          document.body.classList.add('pp-has-app-bar');
          this.setBarHeight();
        }

        disconnectedCallback() {
          if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
          if (this.observer) this.observer.disconnect();
          if (this.resizeObserver) this.resizeObserver.disconnect();
          (this.unsubscribers || []).forEach((unsubscribe) => typeof unsubscribe === 'function' && unsubscribe());
          clearTimeout(this.addedTimer);
          clearTimeout(this.toastTimer);

          // another bar may already have replaced this one (product swap) - only clean up when none is left
          setTimeout(() => {
            if (document.querySelector('product-app-bar[data-ready]')) return;
            document.body.classList.remove('pp-has-app-bar');
            document.documentElement.style.removeProperty('--pp-bar-height');
          }, 0);
        }

        setBarHeight() {
          const height = this.offsetHeight;
          if (height > 0) document.documentElement.style.setProperty('--pp-bar-height', `${height}px`);
        }

        get isLoading() {
          return this.source.classList.contains('loading');
        }

        get isDisabled() {
          return this.source.hasAttribute('disabled') || this.source.getAttribute('aria-disabled') === 'true';
        }

        sync() {
          const disabled = this.isDisabled && !this.isLoading;
          this.button.toggleAttribute('disabled', disabled);
          this.button.setAttribute('aria-busy', this.isLoading ? 'true' : 'false');

          if (!this.hasAttribute('data-flash')) {
            this.button.removeAttribute('data-added');
            const label = clean(this.source.querySelector('span'));
            if (label && this.text) this.text.textContent = label;
          }

          this.syncPrice();
        }

        syncPrice() {
          if (!this.priceWrap) return;
          const block = this.root.querySelector(`#price-${this.sectionId}`);
          const price = block && block.querySelector('.price');
          if (!price || block.classList.contains('hidden')) {
            this.priceWrap.hidden = true;
            return;
          }

          const onSale = price.classList.contains('price--on-sale');
          const now = onSale
            ? price.querySelector('.price__sale .price-item--last')
            : price.querySelector('.price__regular .price-item--regular');
          const was = onSale ? price.querySelector('.price__sale s.price-item--regular') : null;

          const nowText = clean(now);
          this.priceWrap.hidden = !nowText;
          if (this.now) this.now.textContent = nowText;
          if (this.was) {
            const wasText = clean(was);
            this.was.textContent = wasText;
            this.was.hidden = !wasText;
          }
        }

        forwardClick() {
          if (this.button.hasAttribute('disabled') || this.isLoading) return;
          this.hideToast();

          // let the browser show its own validation message (e.g. gift card recipient fields)
          const form = this.source.form;
          if (form && typeof form.reportValidity === 'function' && !form.reportValidity()) return;

          this.source.click();
        }

        flashAdded() {
          clearTimeout(this.addedTimer);
          this.setAttribute('data-flash', '');
          this.button.setAttribute('data-added', '');
          if (this.text) this.text.textContent = this.addedText;
          this.addedTimer = setTimeout(() => {
            this.removeAttribute('data-flash');
            this.sync();
          }, 2200);
        }

        showError(event) {
          if (!this.toast) return;
          const raw = event.errors || event.message;
          const message =
            typeof raw === 'string' ? raw : raw && typeof raw === 'object' ? Object.values(raw).flat().join(' ') : '';
          if (!message) return;
          this.toast.textContent = message;
          this.toast.hidden = false;
          clearTimeout(this.toastTimer);
          this.toastTimer = setTimeout(() => this.hideToast(), 6000);
        }

        hideToast() {
          if (!this.toast) return;
          clearTimeout(this.toastTimer);
          this.toast.hidden = true;
        }
      }
    );
  }

  /* --------------------------------------------------------------------------------------------
   * Floating back / share buttons and gallery dots
   * ------------------------------------------------------------------------------------------ */
  if (!customElements.get('product-app-ui')) {
    customElements.define(
      'product-app-ui',
      class ProductAppUi extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.sectionId;
          this.backButton = this.querySelector('[data-pp-back]');
          this.shareButton = this.querySelector('[data-pp-share]');
          this.dots = this.querySelector('.pp-gallery-dots');
          this.list = document.getElementById(`Slider-Gallery-${this.sectionId}`);

          this.onClick = (event) => {
            if (event.target.closest('[data-pp-back]')) this.goBack();
            else if (event.target.closest('[data-pp-share]')) this.share();
          };
          this.addEventListener('click', this.onClick);

          // hide the share button when the browser can neither share nor copy
          if (this.shareButton && !navigator.share && !(navigator.clipboard && navigator.clipboard.writeText)) {
            this.shareButton.hidden = true;
          }

          if (this.list && this.dots) {
            this.onScroll = () => {
              if (this.frame) return;
              this.frame = requestAnimationFrame(() => {
                this.frame = null;
                this.updateDots();
              });
            };
            this.onResize = () => {
              clearTimeout(this.resizeTimer);
              this.resizeTimer = setTimeout(() => this.buildDots(), 150);
            };
            this.list.addEventListener('scroll', this.onScroll, { passive: true });
            window.addEventListener('resize', this.onResize);
            this.buildDots();
          }
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          if (this.list && this.onScroll) this.list.removeEventListener('scroll', this.onScroll);
          if (this.onResize) window.removeEventListener('resize', this.onResize);
          clearTimeout(this.resizeTimer);
          clearTimeout(this.copiedTimer);
          if (this.frame) cancelAnimationFrame(this.frame);
        }

        get slides() {
          if (!this.list) return [];
          return Array.from(this.list.children).filter((item) => item.offsetWidth > 0);
        }

        buildDots() {
          const count = this.slides.length;
          this.dots.innerHTML = '';
          this.dots.hidden = count < 2;
          for (let i = 0; i < count; i++) this.dots.appendChild(document.createElement('span'));
          this.updateDots();
        }

        updateDots() {
          const dots = this.dots.children;
          if (!dots.length) return;
          const width = this.list.clientWidth || 1;
          const index = Math.min(dots.length - 1, Math.max(0, Math.round(Math.abs(this.list.scrollLeft) / width)));
          Array.from(dots).forEach((dot, i) => {
            if (i === index) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        }

        goBack() {
          let sameSite = false;
          try {
            sameSite = document.referrer && new URL(document.referrer).origin === window.location.origin;
          } catch (e) {
            sameSite = false;
          }
          if (sameSite && window.history.length > 1) {
            window.history.back();
          } else {
            window.location.href = this.dataset.fallbackUrl || '/collections/all';
          }
        }

        async share() {
          const url = window.location.href;
          const title = this.dataset.title || document.title;

          if (navigator.share) {
            try {
              await navigator.share({ title, url });
            } catch (e) {
              /* dismissed by the customer */
            }
            return;
          }

          if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
              await navigator.clipboard.writeText(url);
              this.showCopied();
            } catch (e) {
              /* clipboard blocked */
            }
          }
        }

        showCopied() {
          const button = this.shareButton;
          if (!button) return;
          const original = button.innerHTML;
          const label = button.getAttribute('aria-label');
          button.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
          button.setAttribute('aria-label', this.dataset.copiedText || 'Link copied');
          clearTimeout(this.copiedTimer);
          this.copiedTimer = setTimeout(() => {
            button.innerHTML = original;
            button.setAttribute('aria-label', label);
          }, 1800);
        }
      }
    );
  }
})();
